import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Button, Form, Loader, Modal } from "semantic-ui-react";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import Utils from "@teamprevent/common/utils";

import { FormProvider, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import * as moment from "moment";

import { lightFormat, isDate } from "date-fns";

import { SAVE_EMPLOYEERECORD } from "../actiontypes";
import { AppState, parseEmployeeRecordModelToEditModel } from "../model";
import { CustomerState } from "../../customer";
import { basePathSelector } from "../selectors";
import { saveEmployeeRecord } from "../actions";

import { employeeRecordSchemaFactory, EmployeeRecordFormModel } from "../schema";
import { useCustomerListModel } from "../../customer/hooks";

import { ConfirmTerminationDateChangeModal } from "./ConfirmTerminationDateChangeModal";
import { EditEmployeeForm } from "./EditEmployeeForm";

import { push } from "redux-little-router";

type State = AppState & CustomerState;

export const EditModal: React.FunctionComponent = () => {
    const [initialModel, setInitialModel] = React.useState<EmployeeRecordFormModel | null>(null);
    const [showTerminationConfirmationModal, setShowTerminationConfirmationModal] = React.useState<boolean>(false);
    const employeeRecordId = useSelector((state: State) => state.router.params?.employeeRecordId);
    const employeeRecord = useSelector((state: State) => state.employeeRecordsDetail?.employeeRecords?.find(erd => erd.id === employeeRecordId));
    const open = useSelector((state: State) => state.router.pathname?.endsWith("/edit"));
    const basePath = useSelector((state: State) => basePathSelector(state));
    const { customer, isLoading, isLoaded: isCustomerLoaded } = useCustomerListModel();
    const dispatch = useDispatch();

    // Using this ref button to submit the form so that we can trigger validation properly
    const refSubmitButton = React.useRef<HTMLButtonElement>(null);

    const { handleSubmit, reset, ...methods } = useForm<
        Omit<EmployeeRecordFormModel, "dateOfBirth" | "terminationDate"> & { dateOfBirth?: Date | string; terminationDate?: Date | string},
        any,
        EmployeeRecordFormModel>({
            resolver: zodResolver(employeeRecordSchemaFactory()),
            defaultValues: {
                gender: ""
            },
            shouldFocusError: true,
            mode: "onBlur"
        });

    const workGroups = methods.watch("workGroups");
    const terminationDate = methods.watch("terminationDate");

    React.useEffect(() => {
        if (employeeRecord && isCustomerLoaded && customer) {
            setInitialModel(parseEmployeeRecordModelToEditModel(employeeRecord, customer));
        }
    }, [employeeRecord, isCustomerLoaded, customer]);

    React.useEffect(() => {
        if (initialModel) {
            reset({
                ...initialModel,
                dateOfBirth: isDate(initialModel.dateOfBirth) ? lightFormat(initialModel.dateOfBirth as Date, "yyyy-MM-dd") : "",
                terminationDate: isDate(initialModel.terminationDate) ? lightFormat(initialModel.terminationDate as Date, "yyyy-MM-dd") : "" });
        }
    }, [initialModel, reset]);

    const save = (model: EmployeeRecordFormModel) => dispatch(saveEmployeeRecord(model, basePath));

    const closeEditModal = React.useCallback(() => {
        reset();
        setInitialModel(null);
        setShowTerminationConfirmationModal(false);
        dispatch(push(`${basePath}/${employeeRecordId}`));
    }, [basePath, dispatch, employeeRecordId, reset]);

    const submitForm = React.useCallback((isTerminationDateAccepted: boolean) => {
        if (isTerminationDateAccepted === false) {
            const newTerminationDate = moment(terminationDate);
            const previousTerminationDate = moment(initialModel?.terminationDate);
            if (newTerminationDate.isValid() && !newTerminationDate.isSame(previousTerminationDate)) {
                setShowTerminationConfirmationModal(true);
                return;
            }
        }

        // Programmatically submitting the form
        refSubmitButton?.current?.click();
    }, [initialModel?.terminationDate, terminationDate]);

    const closeTerminationConfirmationModal = React.useCallback(() => {
        setShowTerminationConfirmationModal(false);
    },[]);

    const acceptTerminationDateAndSubmit = React.useCallback(() => {
        submitForm(true);
    }, [submitForm]);

    const checkTerminationDateAndSubmit = React.useCallback(() => {
        submitForm(false);
    }, [submitForm]);

    const getSaveButton = React.useCallback(() => {
        if (!initialModel?.workGroups || !workGroups) {
            return null;
        }
        const haveWorkGroupsChanged = !Utils.IsStringArraysContentEqual(initialModel.workGroups?.map(wg => wg.id), workGroups?.map(wg => wg.id));
        return haveWorkGroupsChanged
            ? (<ConfirmedButton
                content="Save"
                onClick={checkTerminationDateAndSubmit}
                promptText="By adding or removing a work group the system will update Health Surveillance cases accordingly.
                Are you sure you want to do this? Type 'EDIT' and click confirm to edit the employee record."
                typeToConfirmText="EDIT"
                headingText="Edit Employee Record"
            />)
            : <IsLoadingButton requestNames={SAVE_EMPLOYEERECORD} content="Save" onClick={checkTerminationDateAndSubmit} />;
    }, [initialModel?.workGroups, workGroups, checkTerminationDateAndSubmit]);

    if (isLoading || isCustomerLoaded === false || !initialModel) {
        return <Loader active />;
    }

    return (
        <Modal as={Form} size="small" open={open} onClose={closeEditModal} onSubmit={handleSubmit(save)} closeOnDimmerClick={false}>
            <FormProvider handleSubmit={handleSubmit} reset={reset} {...methods}>
                <Modal.Header>Edit Employee Record</Modal.Header>
                <Modal.Content>
                    <EditEmployeeForm initialModel={initialModel} customer={customer} />
                </Modal.Content>
                <ConfirmTerminationDateChangeModal
                    open={showTerminationConfirmationModal}
                    close={closeTerminationConfirmationModal}
                    submit={acceptTerminationDateAndSubmit}
                />
                <Modal.Actions>
                    <Button onClick={closeEditModal}>Cancel</Button>
                    {getSaveButton()}
                    <button hidden={true} ref={refSubmitButton} type={"submit"} />
                </Modal.Actions>
            </FormProvider>
        </Modal>
    );

};
